/**
 * "Live tracking down since X" chip (#254). Rendered in the layer panel next to a
 * layer whose backing ingest daemon has gone dark (per `deriveLayerFeedHealth`).
 *
 * Truth-in-feeds + doctrine: this is honest chrome, not a map marker — it states
 * plainly that what's on the map is last-known, not live. It is STATIC (never
 * pulses; the motion budget is reserved for live-camera + L3/L4 anomaly markers),
 * and it uses a fault-orange register distinct from the amber MutedChip so "the
 * feed broke" never reads as "you muted this".
 */

/** Humanize an elapsed span into a compact age label (m / h / d). */
export function humanizeFeedDownAge(ageMs: number): string {
  const mins = Math.max(0, Math.floor(ageMs / 60_000));
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 48) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

/** Absolute local time of the last live heartbeat, for the tooltip. */
function sinceLabel(downSinceMs: number): string {
  const d = new Date(downSinceMs);
  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const day = d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  return `${day} ${time}`;
}

interface FeedDownChipProps {
  /** Epoch ms of the last live heartbeat across the layer's backing feeds. */
  downSinceMs: number;
  /** The clock the age is judged against (server clock from the last poll). */
  nowMs: number;
  /** Backing workers that are dark — named in the tooltip. */
  workers: ReadonlyArray<string>;
}

export function FeedDownChip({ downSinceMs, nowMs, workers }: FeedDownChipProps): JSX.Element {
  const age = humanizeFeedDownAge(nowMs - downSinceMs);
  const feeds = workers.length > 0 ? workers.join(', ') : 'the backing feed';
  const title =
    `Live tracking down since ${sinceLabel(downSinceMs)} (${age} ago). ` +
    `No heartbeat from ${feeds}. What's shown is the last-known data, not live.`;
  return (
    <span
      data-testid="feed-down-chip"
      className="inline-flex items-center gap-1 text-[9px] font-medium px-1.5 py-0.5 rounded bg-orange-500/15 text-orange-300 border border-orange-500/40 whitespace-nowrap"
      title={title}
    >
      ⚠ down {age}
    </span>
  );
}
